import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, ShoppingBag, Truck, RotateCcw, Shield, CreditCard, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';

export default function CGVPage() {
    return (
        <>
            <SEOHead 
                title="Conditions Générales de Vente | O.C.A Piscine"
                description="Consultez les conditions générales de vente de O.C.A Piscine. Informations sur les commandes, livraisons, retours et garanties."
            />
            <div className="min-h-screen pt-20 pb-12 bg-gradient-to-b from-background to-muted/20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-8">
                        <Link to="/">
                            <Button variant="ghost" className="gap-2 mb-4">
                                <ArrowLeft className="h-4 w-4" />
                                Retour à l'accueil
                            </Button>
                        </Link>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 rounded-2xl bg-primary/10">
                                <Scale className="h-8 w-8 text-primary" />
                            </div>
                            <div>
                                <h1 className="font-heading text-3xl font-bold">Conditions Générales de Vente</h1>
                                <p className="text-muted-foreground">Dernière mise à jour : 8 avril 2026</p>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="prose prose-slate max-w-none space-y-8">
                        
                        {/* Article 1 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <FileText className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 1 - Objet et champ d'application</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre :
                            </p>
                            <div className="bg-muted/50 rounded-xl p-4 mb-4">
                                <p className="font-semibold mb-2">Le Vendeur :</p>
                                <p className="text-sm">
                                    <strong>O.C.A SHOP</strong><br />
                                    SASU au capital de 100 €<br />
                                    Siège social : 8 Rue Roger Chieusse, 83780 Flayosc, France<br />
                                    SIRET : 928 454 719 00016<br />
                                    N° TVA Intracommunautaire : FR51 928 454 719<br />
                                    Email : oca.directionpaca@gmail.com<br />
                                    Téléphone : 06 51 96 14 74<br />
                                    Directeur de la publication : M. David DECAUDIN GUILLOT
                                </p>
                            </div>
                            <p className="text-muted-foreground">
                                Et toute personne physique ou morale (ci-après "le Client") effectuant un achat sur le site 
                                <strong> www.ocapiscine.fr</strong>.
                            </p>
                            <p className="text-muted-foreground">
                                Toute commande passée sur le site implique l'acceptation sans réserve des présentes CGV.
                            </p>
                        </section>

                        {/* Article 2 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <ShoppingBag className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 2 - Produits et services</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Les produits proposés à la vente sont des produits d'entretien et de traitement pour piscines, 
                                notamment de la gamme O.C.A PRO et CTX.
                            </p>
                            <p className="text-muted-foreground mb-4">
                                Les photographies et descriptions des produits sont les plus fidèles possibles mais ne sauraient 
                                engager la responsabilité du Vendeur en cas de légères différences avec le produit livré.
                            </p>
                            <p className="text-muted-foreground">
                                Les offres de produits sont valables dans la limite des stocks disponibles, tels qu'indiqués 
                                lors de la passation de commande.
                            </p>
                        </section>

                        {/* Article 3 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <CreditCard className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 3 - Prix et paiement</h2>
                            </div>
                            <h3 className="font-semibold mb-2">3.1 Prix</h3>
                            <p className="text-muted-foreground mb-4">
                                Les prix sont indiqués en euros toutes taxes comprises (TTC), incluant la TVA au taux en vigueur (20%).
                                Les frais de livraison ne sont pas inclus et sont indiqués avant la validation de la commande.
                            </p>
                            <p className="text-muted-foreground mb-4">
                                Le Vendeur se réserve le droit de modifier ses prix à tout moment. Les produits seront facturés 
                                au prix en vigueur au moment de la validation de la commande.
                            </p>
                            
                            <h3 className="font-semibold mb-2">3.2 Modalités de paiement</h3>
                            <p className="text-muted-foreground mb-4">
                                Le règlement des achats s'effectue par :
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-1">
                                <li>Carte bancaire (Visa, Mastercard) via Stripe (paiement sécurisé)</li>
                                <li>En espèces ou carte bancaire lors du retrait en magasin</li>
                            </ul>
                            <p className="text-muted-foreground">
                                La commande est validée après confirmation du paiement. Une facture est automatiquement 
                                envoyée par email et téléchargeable depuis l'espace client.
                            </p>
                        </section>

                        {/* Article 4 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <Truck className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 4 - Livraison</h2>
                            </div>
                            <h3 className="font-semibold mb-2">4.1 Modes de livraison</h3>
                            <p className="text-muted-foreground mb-4">
                                Deux modes de livraison sont proposés :
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                                <li>
                                    <strong>Retrait en magasin (gratuit)</strong> : 5 Place de la République, 83780 Flayosc.
                                    Le Client sera notifié par email lorsque sa commande sera prête.
                                </li>
                                <li>
                                    <strong>Livraison à domicile via Chronopost</strong> : Les frais de livraison sont calculés 
                                    en fonction du poids et de la destination.
                                </li>
                            </ul>

                            <h3 className="font-semibold mb-2">4.2 Transporteur et conditions d'expédition</h3>
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="font-bold text-blue-700 dark:text-blue-300">Transporteur : CHRONOPOST</span>
                                </div>
                                <ul className="list-disc list-inside text-blue-700 dark:text-blue-300 text-sm space-y-1">
                                    <li><strong>Zone de livraison :</strong> France métropolitaine</li>
                                    <li><strong>Suivi :</strong> Numéro de suivi envoyé par email dès l'expédition</li>
                                    <li><strong>Assurance :</strong> Tous les colis sont assurés pendant le transport</li>
                                </ul>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Les frais de port sont calculés automatiquement lors de la validation de votre panier 
                                en fonction du poids total de votre commande.
                            </p>
                            
                            <h3 className="font-semibold mb-2">4.3 Délais de livraison</h3>
                            <p className="text-muted-foreground mb-4">
                                Les délais de livraison sont donnés à titre indicatif :
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-1">
                                <li><strong>Retrait en magasin :</strong> 24 à 48 heures ouvrées</li>
                                <li><strong>Chronopost Express :</strong> 24 à 48 heures (jours ouvrés)</li>
                                <li><strong>Chronopost Classic :</strong> 2 à 4 jours ouvrés</li>
                            </ul>
                            <p className="text-muted-foreground mb-4">
                                Les commandes passées avant 14h00 (hors week-ends et jours fériés) sont généralement 
                                expédiées le jour même.
                            </p>
                            <p className="text-muted-foreground">
                                En cas de retard de livraison supérieur à 7 jours, le Client peut annuler sa commande 
                                et obtenir un remboursement intégral.
                            </p>
                            
                            <h3 className="font-semibold mb-2">4.4 Réception et vérification</h3>
                            <p className="text-muted-foreground mb-4">
                                À la réception, le Client doit vérifier l'état du colis <strong>en présence du livreur</strong>. 
                                En cas de dommage apparent (colis ouvert, écrasé, mouillé), le Client doit :
                            </p>
                            <ol className="list-decimal list-inside text-muted-foreground mb-4 space-y-1">
                                <li>Refuser le colis ou émettre des réserves écrites précises sur le bon de livraison</li>
                                <li>Prendre des photos des dommages</li>
                                <li>Contacter O.C.A Piscine dans les 48 heures au 06 51 96 14 74</li>
                            </ol>
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                                <p className="text-amber-700 dark:text-amber-300 text-sm">
                                    <strong>⚠️ Attention :</strong> Sans réserves émises à la livraison, aucune réclamation 
                                    pour dommage lié au transport ne pourra être acceptée.
                                </p>
                            </div>
                        </section>

                        {/* Article 5 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <RotateCcw className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 5 - Droit de rétractation</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Conformément à l'article L221-18 du Code de la consommation, le Client dispose d'un délai 
                                de <strong>14 jours calendaires</strong> à compter de la réception du produit pour exercer 
                                son droit de rétractation, sans avoir à justifier de motifs ni à payer de pénalités.
                            </p>
                            
                            <h3 className="font-semibold mb-2">5.1 Modalités de rétractation</h3>
                            <p className="text-muted-foreground mb-4">
                                Pour exercer ce droit, le Client doit notifier sa décision par :
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-1">
                                <li>Email : oca.directionpaca@gmail.com</li>
                                <li>Courrier : O.C.A SHOP, 8 Rue Roger Chieusse, 83780 Flayosc</li>
                            </ul>
                            
                            <h3 className="font-semibold mb-2">5.2 Retour des produits</h3>
                            <p className="text-muted-foreground mb-4">
                                Les produits doivent être retournés dans leur emballage d'origine, complets et en parfait état, 
                                dans un délai maximum de 14 jours suivant la notification de rétractation.
                            </p>
                            <p className="text-muted-foreground mb-4">
                                Les frais de retour sont à la charge du Client.
                            </p>
                            
                            <h3 className="font-semibold mb-2">5.3 Remboursement</h3>
                            <p className="text-muted-foreground mb-4">
                                Le remboursement sera effectué dans un délai de 14 jours suivant la réception des produits 
                                retournés, par le même moyen de paiement que celui utilisé pour la commande initiale.
                            </p>
                            
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                                <h3 className="font-semibold mb-2 text-amber-800 dark:text-amber-200">5.4 Exclusions</h3>
                                <p className="text-amber-700 dark:text-amber-300 text-sm">
                                    Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation 
                                    ne peut être exercé pour les produits descellés après livraison et ne pouvant être 
                                    renvoyés pour des raisons d'hygiène ou de protection de la santé (produits chimiques ouverts).
                                </p>
                            </div>
                        </section>

                        {/* Article 6 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 6 - Garanties</h2>
                            </div>
                            <h3 className="font-semibold mb-2">6.1 Garantie légale de conformité</h3>
                            <p className="text-muted-foreground mb-4">
                                Conformément aux articles L217-4 et suivants du Code de la consommation, le Vendeur est 
                                tenu de livrer un bien conforme au contrat et répond des défauts de conformité existant 
                                lors de la délivrance. Cette garantie s'applique pendant <strong>2 ans</strong> à compter 
                                de la délivrance du bien.
                            </p>
                            
                            <h3 className="font-semibold mb-2">6.2 Garantie des vices cachés</h3>
                            <p className="text-muted-foreground mb-4">
                                Conformément aux articles 1641 et suivants du Code civil, le Vendeur est tenu de la garantie 
                                à raison des défauts cachés de la chose vendue qui la rendent impropre à l'usage auquel on 
                                la destine, ou qui diminuent tellement cet usage que l'acheteur ne l'aurait pas acquise.
                            </p>
                            
                            <h3 className="font-semibold mb-2">6.3 Mise en œuvre des garanties</h3>
                            <p className="text-muted-foreground">
                                Pour faire valoir ses garanties, le Client doit contacter le Vendeur par email à 
                                oca.directionpaca@gmail.com en joignant la facture d'achat et des photos du produit défectueux.
                            </p>
                        </section>

                        {/* Article 7 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 7 - Responsabilité</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Le Vendeur ne saurait être tenu responsable de l'inexécution du contrat en cas de :
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-1">
                                <li>Force majeure</li>
                                <li>Faute du Client (mauvaise utilisation des produits)</li>
                                <li>Fait imprévisible et insurmontable d'un tiers</li>
                            </ul>
                            <p className="text-muted-foreground">
                                Les produits chimiques pour piscine doivent être utilisés conformément aux instructions 
                                figurant sur leur emballage. Le Vendeur décline toute responsabilité en cas de mauvaise 
                                utilisation des produits.
                            </p>
                        </section>

                        {/* Article 8 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 8 - Protection des données personnelles</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi 
                                Informatique et Libertés, le Client dispose d'un droit d'accès, de rectification, 
                                de suppression et d'opposition sur ses données personnelles.
                            </p>
                            <p className="text-muted-foreground mb-4">
                                Les données collectées sont nécessaires au traitement des commandes et ne sont jamais 
                                transmises à des tiers à des fins commerciales.
                            </p>
                            <p className="text-muted-foreground">
                                Pour exercer ces droits, le Client peut contacter le Vendeur à : oca.directionpaca@gmail.com
                            </p>
                        </section>

                        {/* Article 9 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <Scale className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 9 - Litiges et médiation</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Les présentes CGV sont soumises au droit français. En cas de litige, une solution amiable 
                                sera recherchée avant toute action judiciaire.
                            </p>
                            <p className="text-muted-foreground mb-4">
                                Conformément à l'article L612-1 du Code de la consommation, le Client peut recourir 
                                gratuitement à un médiateur de la consommation en vue de la résolution amiable du litige.
                            </p>
                            <div className="bg-muted/50 rounded-xl p-4">
                                <p className="font-semibold mb-2">Médiateur de la consommation :</p>
                                <p className="text-sm text-muted-foreground">
                                    CM2C (Centre de Médiation de la Consommation de Conciliateurs de Justice)<br />
                                    14 rue Saint Jean, 75017 Paris<br />
                                    Site : <a href="https://www.cm2c.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.cm2c.net</a>
                                </p>
                            </div>
                            <p className="text-muted-foreground mt-4">
                                Plateforme de règlement en ligne des litiges de la Commission européenne :<br />
                                <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                    https://ec.europa.eu/consumers/odr
                                </a>
                            </p>
                        </section>

                        {/* Article 10 */}
                        <section className="bg-card rounded-2xl p-6 shadow-sm border">
                            <div className="flex items-center gap-2 mb-4">
                                <Mail className="h-5 w-5 text-primary" />
                                <h2 className="text-xl font-bold m-0">Article 10 - Contact</h2>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Pour toute question relative aux présentes CGV ou à une commande, le Client peut contacter 
                                le service client :
                            </p>
                            <div className="bg-primary/5 rounded-xl p-4">
                                <p className="text-sm">
                                    <strong>O.C.A SHOP</strong><br />
                                    8 Rue Roger Chieusse, 83780 Flayosc<br />
                                    Email : oca.directionpaca@gmail.com<br />
                                    Téléphone : 06 51 96 14 74<br />
                                    Horaires : Lundi au Samedi, 9h-12h / 14h-18h
                                </p>
                            </div>
                        </section>

                    </div>

                    {/* Back to top */}
                    <div className="mt-12 text-center">
                        <Link to="/">
                            <Button variant="outline" className="rounded-full">
                                Retour à l'accueil
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
}
